import { useMemo, useState } from 'react'
import { EnrollmentState } from 'src/graphql.generated'
import { isSuccessWithData, useFirebase } from 'src/firebase'
import { Program } from 'src/legacy/models/firebase'
import { HomeViewParticipantFragment } from 'src/participantsHome/searchParticipants.generated'
import sortParticipants, { HOME_VIEW_ENROLLMENT_STATES, ProgramsByID } from 'src/participantsHome/sortParticipants'
import { AdvancedPatientSummary } from '../api/types'
import {
  GetAdvancedCaseloadEnrollmentsQuery,
  useGetAdvancedCaseloadEnrollmentsQuery,
} from './advancedCaseload.generated'
import { ENROLLMENT_STATES } from './enrollmentStates'

export type CaseloadEnrollment = GetAdvancedCaseloadEnrollmentsQuery['patients'][number]

export type CaseloadGroup = { key: string; label: string; summaries: AdvancedPatientSummary[] }

export type CaseloadModule = { key: string; label: string; groups: CaseloadGroup[] }

const OTHER_MODULE_KEY = 'other'

// Standard's Wrapping up module already has a "Post-Program" theme, for participants past their last week.
const OTHER_LABELS: Partial<Record<EnrollmentState, string>> = { [EnrollmentState.PostProgram]: 'Program ended' }

const inStandardList = (row: CaseloadEnrollment | undefined): row is CaseloadEnrollment =>
  HOME_VIEW_ENROLLMENT_STATES.some((entry) => entry.enrollmentState === row?.enrollment.state)

// Standard's modules and themes (ADR-222); rows keep the attention order they arrive in.
export const groupCaseloadByModule = (
  summaries: AdvancedPatientSummary[],
  enrollments: Record<string, CaseloadEnrollment>,
  programs: ProgramsByID
): CaseloadModule[] => {
  const inAttentionOrder = (uids: string[]) => {
    const wanted = new Set(uids)
    return summaries.filter((summary) => wanted.has(summary.uid))
  }

  const standardRows = summaries
    .map((summary) => enrollments[summary.uid])
    .filter(inStandardList)
    .map((enrollment) => enrollment as Pick<HomeViewParticipantFragment, 'uid'> as HomeViewParticipantFragment)

  const modules = sortParticipants(standardRows, programs).map((participantModule) => ({
    key: participantModule.moduleKey,
    label: participantModule.moduleLabel,
    groups: participantModule.groups.map((group) => ({
      key: group.key,
      label: group.label,
      summaries: inAttentionOrder(group.participants.map((participant) => participant.uid)),
    })),
  }))

  // States Standard's list leaves out; Unknown takes the rest: no patient from `server`, or a state this build doesn't know.
  const outsideStandardList = summaries.filter((summary) => !inStandardList(enrollments[summary.uid]))
  const stateGroups = ENROLLMENT_STATES.map(({ id, label }) => ({
    key: id,
    label: OTHER_LABELS[id] ?? label,
    summaries: outsideStandardList.filter((summary) => enrollments[summary.uid]?.enrollment.state === id),
  }))
  const placed = new Set(stateGroups.flatMap((group) => group.summaries.map((summary) => summary.uid)))
  const otherGroups = [
    ...stateGroups,
    { key: 'unknown', label: 'Unknown', summaries: outsideStandardList.filter((summary) => !placed.has(summary.uid)) },
  ].filter((group) => group.summaries.length > 0)

  return otherGroups.length > 0 ? [...modules, { key: OTHER_MODULE_KEY, label: 'Other', groups: otherGroups }] : modules
}

// Null, so the list stays flat, until both reads land and when the read fails: one participant without an enrollment nulls it.
export const useCaseloadModules = (
  uids: string[],
  summaries: AdvancedPatientSummary[],
  enabled: boolean
): CaseloadModule[] | null => {
  const { data, previousData, loading } = useGetAdvancedCaseloadEnrollmentsQuery({
    variables: { uids },
    skip: !enabled || uids.length === 0,
  })
  // A changed caseload re-reads; keep the last groups meanwhile rather than flash the list flat.
  const reply = data ?? (loading ? previousData : undefined)
  const programsRequest = useFirebase<Record<string, Program>>(enabled ? 'programs' : null)
  const programs = isSuccessWithData(programsRequest) ? programsRequest.data : null

  const enrollments = useMemo(
    () => (reply?.patients ? Object.fromEntries(reply.patients.map((patient) => [patient.uid, patient])) : null),
    [reply]
  )

  return enabled && enrollments && programs ? groupCaseloadByModule(summaries, enrollments, programs) : null
}

export const useCollapsedThemes = () => {
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set())
  const toggle = (key: string) =>
    setCollapsed((current) => {
      const next = new Set(current)
      if (!next.delete(key)) {
        next.add(key)
      }
      return next
    })
  return [collapsed, toggle] as const
}
